import React, { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';

/**
 * PageHeader component props
 */
export interface PageHeaderProps {
  /** Page title (required) */
  title: string;
  /** Optional subtitle or description */
  subtitle?: string;
  /** Optional breadcrumb navigation */
  breadcrumb?: {
    label: string;
    href: string;
    icon?: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
  };
  /** Optional action buttons (rendered on right side) */
  actions?: ReactNode;
  /** Optional badges (rendered above title) */
  badges?: ReactNode;
  /** Optional className */
  className?: string;
}

/**
 * PageHeader component
 *
 * Standardized page header with breadcrumb support for consistent title alignment
 * across list and detail pages.
 *
 * @example
 * ```tsx
 * // List page (no breadcrumb)
 * <PageHeader
 *   title="Objetivos"
 *   subtitle="15 objetivos cadastrados"
 *   actions={<CreateButton />}
 * />
 *
 * // Detail page (with breadcrumb)
 * <PageHeader
 *   title={objective.title}
 *   breadcrumb={{
 *     label: 'Voltar para Objetivos',
 *     href: '/okr/objectives',
 *     icon: ArrowLeftIcon
 *   }}
 *   badges={
 *     <>
 *       <Badge variant="level">{objective.level}</Badge>
 *       <Badge variant="status">{objective.status}</Badge>
 *     </>
 *   }
 *   actions={
 *     <>
 *       <EditButton />
 *       <ArchiveButton />
 *     </>
 *   }
 * />
 * ```
 */
export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  breadcrumb,
  actions,
  badges,
  className
}) => {
  const navigate = useNavigate();

  return (
    <div className={clsx('space-y-4', className)}>
      {/* Breadcrumb */}
      {breadcrumb && (
        <nav className="flex items-center space-x-2 text-sm" aria-label="Breadcrumb">
          <button
            onClick={() => navigate(breadcrumb.href)}
            className="flex items-center text-gray-600 hover:text-primary-600 transition-colors"
            type="button"
          >
            {breadcrumb.icon && (
              <breadcrumb.icon className="h-4 w-4 mr-1" aria-hidden="true" />
            )}
            <span>{breadcrumb.label}</span>
          </button>
        </nav>
      )}

      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          {badges && (
            <div className="mb-2 flex items-center gap-2">
              {badges}
            </div>
          )}
          <h1 className="font-montserrat text-2xl font-bold text-gray-900 sm:text-3xl">
            {title}
          </h1>
          {subtitle && (
            <p className="mt-1 text-sm text-gray-600">
              {subtitle}
            </p>
          )}
        </div>
        {actions && (
          <div className="flex flex-shrink-0 gap-2">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
};
